/**
 * Typed fetch client for the Yalıda Davet API.
 *
 * Shared by the web SPA (apps/web) and the Expo app (apps/mobile): each app
 * calls `createApiClient(baseUrl)` with its own base URL and wires the
 * session token in through `setTokenGetter`.
 */
import type {
  MansionCard, MansionDetail, Quote, BookingDto, ConversationDto, MessageDto,
  SearchFilters, User, UserRole, BookingStatus,
} from './types.js';
import type {
  AdminStats, AdminListingRow, AdminListingDetail, AdminListingAction, AdminListingPatch,
  AdminUserRow, AdminBookingRow, AdminDocumentRow, Paged, QcRunDto, QcScope, QcStepResponse,
} from './admin.js';

export interface ApiClientOptions {
  /** Extra headers on every request — e.g. the CI service token (`X-Admin-Token`). */
  extraHeaders?: () => Record<string, string> | undefined;
}

/**
 * A file to upload. Browsers pass a `File`/`Blob`; React Native passes the
 * `{ uri, name, type }` descriptor that its FormData implementation accepts.
 */
export type UploadFile = Blob | { uri: string; name: string; type: string };

export class ApiRequestError extends Error {
  constructor(public status: number, public code: string, message: string, public details?: unknown) {
    super(message);
    this.name = 'ApiRequestError';
  }
}

/** SearchFilters → URLSearchParams, dropping empty values. */
export function toQueryString(filters: object): string {
  const params = new URLSearchParams();
  Object.entries(filters).forEach(([k, v]) => {
    if (v === undefined || v === null || v === '' || v === false) return;
    params.set(k, String(v));
  });
  return params.toString();
}

export interface SearchResponse {
  items: MansionCard[];
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
  appliedTier: 1 | 2 | 3 | null;
  requiredGardenM2: number | null;
  summaryTr: string;
}

const appendFile = (fd: FormData, key: string, file: UploadFile) =>
  // React Native's FormData takes the descriptor object directly.
  fd.append(key, file as unknown as Blob);

export function createApiClient(baseUrl: string, options: ApiClientOptions = {}) {
  /** Injected by the auth bridge so non-hook code can authenticate. */
  let tokenGetter: (() => Promise<string | null>) | null = null;
  const setTokenGetter = (fn: (() => Promise<string | null>) | null) => {
    tokenGetter = fn;
  };

  async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
    const headers = new Headers(init.headers);
    if (!(init.body instanceof FormData) && init.body) headers.set('Content-Type', 'application/json');

    const token = await tokenGetter?.().catch(() => null);
    if (token) headers.set('Authorization', `Bearer ${token}`);
    Object.entries(options.extraHeaders?.() ?? {}).forEach(([k, v]) => headers.set(k, v));

    const res = await fetch(`${baseUrl}${path}`, { ...init, headers });
    if (res.status === 204) return undefined as T;

    const payload = await res.json().catch(() => ({}));
    if (!res.ok) {
      throw new ApiRequestError(
        res.status,
        (payload as any).error ?? 'UNKNOWN',
        (payload as any).message ?? 'Bir hata oluştu.',
        (payload as any).details,
      );
    }
    return payload as T;
  }

  const api = {
    health: () => request<{ status: string }>('/health'),

    eventTypes: () => request<{ items: any[] }>('/event-types'),
    tiers: () => request<{ tiers: any[] }>('/mansions/tiers'),

    searchMansions: (filters: Partial<SearchFilters>) =>
      request<SearchResponse>(`/mansions?${toQueryString(filters)}`),

    featured: (limit = 8) => request<{ items: MansionCard[] }>(`/mansions/featured?limit=${limit}`),

    mansion: (idOrSlug: string, opts?: { guestCount?: number; eventTypeId?: string }) =>
      request<MansionDetail>(`/mansions/${idOrSlug}?${toQueryString(opts ?? {})}`),

    availability: (id: string, from: string, to: string) =>
      request<{
        blockedDates: { date: string; reason: string | null }[];
        bookedRanges: { from: string; to: string }[];
        maxCapacity: number;
        gardenSizeM2: number;
        minNights: number;
        checkoutDeadline: string;
      }>(`/mansions/${id}/availability?from=${from}&to=${to}`),

    myListings: () => request<{ items: any[] }>('/mansions/mine/list'),
    createMansion: (body: unknown) =>
      request<{ id: string; slug: string; status: string }>('/mansions', { method: 'POST', body: JSON.stringify(body) }),

    favorite: (id: string, on: boolean) =>
      request<{ isFavorite: boolean }>(`/mansions/${id}/favorite`, { method: on ? 'POST' : 'DELETE' }),

    quote: (body: {
      mansionId: string; checkin: string; checkout: string; guestCount: number; eventTypeId: string; addonIds: string[];
    }) => request<Quote>('/bookings/quote', { method: 'POST', body: JSON.stringify(body) }),

    createBooking: (body: unknown) =>
      request<{ id: string; referenceCode: string; quote: Quote }>('/bookings', { method: 'POST', body: JSON.stringify(body) }),

    myBookings: () => request<{ items: BookingDto[] }>('/bookings'),
    booking: (id: string) => request<BookingDto>(`/bookings/${id}`),
    hostBookings: () => request<{ items: any[] }>('/bookings/host'),
    updateBookingStatus: (id: string, status: string, reason?: string) =>
      request<{ id: string; status: string }>(`/bookings/${id}/status`, { method: 'PATCH', body: JSON.stringify({ status, reason }) }),

    conversations: (q?: string) =>
      request<{ items: ConversationDto[] }>(`/messages/conversations${q ? `?q=${encodeURIComponent(q)}` : ''}`),
    messages: (conversationId: string) =>
      request<{ items: MessageDto[]; conversationId: string; mansionId: string | null }>(
        `/messages/conversations/${conversationId}/messages`,
      ),
    sendMessage: (conversationId: string, body: { body: string; messageType?: string; attachmentR2Key?: string; attachmentName?: string }) =>
      request<MessageDto>(`/messages/conversations/${conversationId}/messages`, { method: 'POST', body: JSON.stringify(body) }),
    startConversation: (mansionId: string, body: string) =>
      request<{ conversationId: string }>('/messages/start', { method: 'POST', body: JSON.stringify({ mansionId, body }) }),
    unreadCount: () => request<{ count: number }>('/messages/unread-count'),

    me: () => request<User>('/me'),
    favorites: () => request<{ items: MansionCard[] }>('/me/favorites'),
    becomeHost: () => request<{ role: string }>('/me/become-host', { method: 'POST' }),
    registerDevice: (fcmToken: string, platform = 'web') =>
      request<{ registered: boolean }>('/me/devices', { method: 'POST', body: JSON.stringify({ fcmToken, platform }) }),

    uploadPhotos: (files: UploadFile[]) => {
      const fd = new FormData();
      files.forEach((f) => appendFile(fd, 'files', f));
      return request<{ items: { id: string; url: string; name: string }[] }>('/uploads/photos', { method: 'POST', body: fd });
    },
    uploadDocument: (mansionId: string, docType: string, file: UploadFile) => {
      const fd = new FormData();
      appendFile(fd, 'file', file);
      fd.append('mansionId', mansionId);
      fd.append('docType', docType);
      return request<{ id: string; fileName: string; status: string }>('/uploads/documents', { method: 'POST', body: fd });
    },
    uploadChatAttachment: (file: UploadFile) => {
      const fd = new FormData();
      appendFile(fd, 'file', file);
      return request<{ key: string; url: string; name: string; type: string }>('/uploads/chat-attachment', { method: 'POST', body: fd });
    },

    /* ------------------------------ Admin panel ------------------------------ */
    admin: {
      stats: () => request<AdminStats>('/admin/stats'),

      listings: (q: { q?: string; status?: string; qc?: string; page?: number; pageSize?: number; sort?: string } = {}) =>
        request<Paged<AdminListingRow>>(`/admin/listings?${toQueryString(q)}`),
      listing: (id: string) => request<AdminListingDetail>(`/admin/listings/${id}`),
      updateListing: (id: string, patch: AdminListingPatch) =>
        request<{ updated: number }>(`/admin/listings/${id}`, { method: 'PATCH', body: JSON.stringify(patch) }),
      listingAction: (ids: string[], action: AdminListingAction, note?: string) =>
        request<{ updated: number }>('/admin/listings/actions', { method: 'POST', body: JSON.stringify({ ids, action, note }) }),
      notifyHost: (id: string, message: string) =>
        request<{ notified: boolean }>(`/admin/listings/${id}/notify-host`, { method: 'POST', body: JSON.stringify({ message }) }),

      users: (q: { q?: string; role?: string; page?: number } = {}) =>
        request<Paged<AdminUserRow>>(`/admin/users?${toQueryString(q)}`),
      updateUser: (id: string, patch: { role?: UserRole; isBanned?: boolean }) =>
        request<{ updated: number }>(`/admin/users/${id}`, { method: 'PATCH', body: JSON.stringify(patch) }),

      bookings: (q: { q?: string; status?: string; page?: number } = {}) =>
        request<Paged<AdminBookingRow>>(`/admin/bookings?${toQueryString(q)}`),
      updateBooking: (id: string, status: BookingStatus) =>
        request<{ updated: number }>(`/admin/bookings/${id}`, { method: 'PATCH', body: JSON.stringify({ status }) }),

      documents: (q: { status?: string; page?: number } = {}) =>
        request<Paged<AdminDocumentRow>>(`/admin/documents?${toQueryString(q)}`),
      reviewDocument: (id: string, status: 'approved' | 'rejected', reviewNote?: string) =>
        request<{ updated: number }>(`/admin/documents/${id}`, { method: 'PATCH', body: JSON.stringify({ status, reviewNote }) }),

      qcRuns: (limit = 20) => request<{ items: QcRunDto[] }>(`/admin/qc/runs?limit=${limit}`),
      qcRun: (id: string) => request<QcRunDto>(`/admin/qc/runs/${id}`),
      startQcRun: (body: { scope?: QcScope; trigger?: 'panel' | 'ci' | 'schedule'; ciUrl?: string; commitSha?: string } = {}) =>
        request<QcRunDto>('/admin/qc/runs', { method: 'POST', body: JSON.stringify(body) }),
      qcStep: (runId: string) => request<QcStepResponse>(`/admin/qc/runs/${runId}/step`, { method: 'POST' }),
      checkListing: (id: string) => request<QcStepResponse['processed'][number]>(`/admin/listings/${id}/check`, { method: 'POST' }),
    },
  };

  return { api, setTokenGetter };
}

export type Api = ReturnType<typeof createApiClient>['api'];
